import { useGetRenewVisaBasicInformationDocumentGroupInfo, useGetRenewVisaDocumentGroupInfo } from "@/api/services/main/renew-visa";
import { IconCheck } from "@/assets/icons";
import { findRenewVisaDocumentStepGroup } from "@/features/renew-visa/utils/findDocumentStepGroup";
import { background, button, text } from "@/theme/colors";
import { DOCUMENT_STEP_KEYS, RENEW_VISA_DOCUMENT_STEP_STATUS } from "@/types/enum";
import { toNumber } from "@/utils/number";
import { Typography } from "@mui/material";
import Stack from "@mui/material/Stack";
import Step from "@mui/material/Step";
import StepConnector, { stepConnectorClasses } from "@mui/material/StepConnector";
import { StepIconProps } from "@mui/material/StepIcon";
import StepLabel from "@mui/material/StepLabel";
import Stepper from "@mui/material/Stepper";
import { styled } from "@mui/material/styles";
import { FC, useMemo } from "react";

const ColorlibConnector = styled(StepConnector)(() => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: {
    top: 22,
  },
  [`&.${stepConnectorClasses.active}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      backgroundColor: `${button.primary}`,
    },
  },
  [`&.${stepConnectorClasses.completed}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      backgroundColor: `${button.primary}`,
    },
  },
  [`& .${stepConnectorClasses.line}`]: {
    height: 3,
    border: 0,
    backgroundColor: `${background.disable}`,
    borderRadius: 1,
  },
}));

const ColorlibStepIconRoot = styled("div")<{
  ownerState: { completed?: boolean; active?: boolean };
}>(() => ({
  backgroundColor: `${background.disable}`,
  zIndex: 1,
  color: `${text.tertiary}`,
  width: 40,
  height: 40,
  display: "flex",
  borderRadius: "50%",
  justifyContent: "center",
  alignItems: "center",
  variants: [
    {
      props: ({ ownerState }) => ownerState.active,
      style: {
        backgroundColor: `${background.white}`,
        border: `4px solid ${button.primary}`,
        color: `${button.primary}`,
      },
    },
    {
      props: ({ ownerState }) => ownerState.completed,
      style: {
        backgroundColor: `${button.primary}`,
        color: `${text.white}`,
      },
    },
  ],
}));

function ColorlibStepIcon(props: StepIconProps) {
  const { active, completed, className } = props;

  return (
    <ColorlibStepIconRoot ownerState={{ completed, active }} className={className}>
      {completed ? <IconCheck width={20} height={20} /> : props.icon}
    </ColorlibStepIconRoot>
  );
}

export type TRenewVisaProgressStepProps = {};

export const RenewVisaProgressStep: FC<TRenewVisaProgressStepProps> = ({}) => {
  const { data: renewVisaDocumentGroup } = useGetRenewVisaDocumentGroupInfo();

  const basicInfoStepGroupData = findRenewVisaDocumentStepGroup(renewVisaDocumentGroup, DOCUMENT_STEP_KEYS.RENEW_VISA_GROUP_BASIC_INFORMATION);
  const applicationStepGroupData = findRenewVisaDocumentStepGroup(renewVisaDocumentGroup, DOCUMENT_STEP_KEYS.RENEW_VISA_GROUP_CREATE_DOCUMENT);
  const onlineSubmitApplicationStepGroupData = findRenewVisaDocumentStepGroup(renewVisaDocumentGroup, DOCUMENT_STEP_KEYS.RENEW_VISA_GROUP_SUBMIT);
  const afterSubmitApplicationStepGroupData = findRenewVisaDocumentStepGroup(
    renewVisaDocumentGroup,
    DOCUMENT_STEP_KEYS.RENEW_VISA_GROUP_AFTER_SUBMIT_APPLICATION,
  );
  const afterSubmitApplicationResultStepGroupData = findRenewVisaDocumentStepGroup(
    renewVisaDocumentGroup,
    DOCUMENT_STEP_KEYS.RENEW_VISA_GROUP_AFTER_SUBMIT_APPLICATION_RESULT,
  );
  const residenceCardInfoStepGroupData = findRenewVisaDocumentStepGroup(
    renewVisaDocumentGroup,
    DOCUMENT_STEP_KEYS.RENEW_VISA_GROUP_UPDATE_RESIDENCE_CARD,
  );

  const { data: basicInfoData } = useGetRenewVisaBasicInformationDocumentGroupInfo({
    renewVisaDocumentStepGroupId: toNumber(basicInfoStepGroupData?.id),
  });

  const stepNames = useMemo(
    () =>
      [
        !!basicInfoData ? basicInfoStepGroupData?.masterDocumentStep.stepName : "",
        applicationStepGroupData?.masterDocumentStep.stepName ?? "",
        onlineSubmitApplicationStepGroupData?.masterDocumentStep.stepName ?? "",
        afterSubmitApplicationStepGroupData?.masterDocumentStep.stepName ?? "",
        afterSubmitApplicationResultStepGroupData?.masterDocumentStep.stepName ?? "",
        residenceCardInfoStepGroupData?.masterDocumentStep.stepName,
      ].filter((item) => !!item),
    [basicInfoStepGroupData, basicInfoData],
  );

  const activeStep = useMemo(() => {
    if (residenceCardInfoStepGroupData?.status === RENEW_VISA_DOCUMENT_STEP_STATUS.COMPLETE) return 6;
    if (afterSubmitApplicationResultStepGroupData?.status === RENEW_VISA_DOCUMENT_STEP_STATUS.COMPLETE) return 5;
    if (afterSubmitApplicationStepGroupData?.status === RENEW_VISA_DOCUMENT_STEP_STATUS.COMPLETE) return 4;
    if (onlineSubmitApplicationStepGroupData?.status === RENEW_VISA_DOCUMENT_STEP_STATUS.COMPLETE) return 3;
    if (applicationStepGroupData?.status === RENEW_VISA_DOCUMENT_STEP_STATUS.COMPLETE) return 2;
    if (basicInfoStepGroupData?.status === RENEW_VISA_DOCUMENT_STEP_STATUS.COMPLETE) return 1;
    return 0;
  }, [
    basicInfoStepGroupData,
    applicationStepGroupData,
    onlineSubmitApplicationStepGroupData,
    afterSubmitApplicationStepGroupData,
    afterSubmitApplicationResultStepGroupData,
    residenceCardInfoStepGroupData,
  ]);

  const isSkipStep = useMemo(() => !!renewVisaDocumentGroup && !!renewVisaDocumentGroup.isSkipStep, [renewVisaDocumentGroup]);

  return (
    <Stack sx={{ width: !!basicInfoData ? "80%" : "70%" }} marginX="auto" spacing={4} pt={1} pb={4}>
      <Stepper
        alternativeLabel
        activeStep={activeStep <= 2 && isSkipStep ? 3 : !!basicInfoData ? activeStep : activeStep > 0 ? activeStep - 1 : activeStep}
        connector={<ColorlibConnector />}
      >
        {stepNames.map((label) => (
          <Step key={label}>
            <StepLabel StepIconComponent={ColorlibStepIcon}>
              <Typography variant="body14">{label}</Typography>
            </StepLabel>
          </Step>
        ))}
      </Stepper>
    </Stack>
  );
};
